/**
 * Collection API Search Integration
 * Fetches products from the search API when year, make, model, submodel query params are present
 * Uses Shopify's section rendering API to get properly styled product cards
 */
(function() {
  // Track current page internally (not in URL)
  let currentPage = 1;

  function getQueryParams(url = null) {
    const searchParams = url ? new URL(url).search : window.location.search;
    const params = new URLSearchParams(searchParams);
    return {
      year: params.get('year'),
      make: params.get('make'),
      model: params.get('model'),
      submodel: params.get('submodel')
    };
  }

  function hasSearchParams(url = null) {
    const params = getQueryParams(url);
    const searchParams = url ? new URL(url).search : window.location.search;
    const urlParams = new URLSearchParams(searchParams);
    // Check if custom filters are enabled via data attribute
    const productGridContainer = document.getElementById('ProductGridContainer');
    const useCustomFilters = productGridContainer?.dataset?.useCustomFilters === 'true';

    if (useCustomFilters) {
      // When custom filters are enabled, always render (filters are optional)
      return true;
    }

    // For other pages, require search params
    return !!(params.year || params.make || params.model || params.submodel ||
              urlParams.get('brand') || urlParams.get('categoryId'));
  }
  
  function getCollectionHandle(url = null) {
    const pathname = url ? new URL(url).pathname : window.location.pathname;
    const parts = pathname.split('/collections/');
    return parts.length > 1 ? parts[1].split('/')[0] : null;
  }
  
  function buildSearchQueryParams(page = null, url = null) {
    const searchParams = url ? new URL(url).search : window.location.search;
    const queryParams = new URLSearchParams(searchParams);

    // Add collection handle derived from pathname (not a URL query param)
    const collectionHandle = getCollectionHandle(url);
    if (collectionHandle) {
      queryParams.set('collection', collectionHandle);
    }

    // Page is managed internally, not in the URL
    queryParams.set('page', page !== null ? page : currentPage);

    return queryParams;
  }
  
  async function fetchSearchAPI(page = null, url = null) {
    const queryParams = buildSearchQueryParams(page, url);
    const apiUrl = `/a/minion/search?${queryParams.toString()}`;
    
    try {
      const data = await window.fetchCached(apiUrl, "collection-api-search");
      
      const filterKeys = ['brands', 'categories', 'facets'];
      if (filterKeys.some((k) => data.hasOwnProperty(k))) {
        window.apiFilterData = window.apiFilterData || {};
        const detail = {};
        for (const k of filterKeys) {
          if (!data.hasOwnProperty(k)) continue;
          const v = data[k] || [];
          window.apiFilterData[k] = v;
          detail[k] = v;
        }
        window.dispatchEvent(new CustomEvent('apiFilterDataUpdated', { detail }));
      }
      
      return {
        handles: data.productHandles || [],
        pagination: data.pagination || null,
        brands: data.brands || [],
        categories: data.categories || [],
        facets: data.facets || []
      };
    } catch (error) {
      console.error('[CollectionAPI] Error fetching search API:', error);
      // Dispatch error event for filter components
      window.dispatchEvent(new CustomEvent('apiFilterDataError', { 
        detail: { error: error.message || 'Failed to load filter data' }
      }));
      return { handles: [], pagination: null, brands: [], categories: [], facets: [] };
    }
  }
  
  async function fetchProductHandles(url = null) {
    // Reset to page 1 for initial load
    currentPage = 1;
    return await fetchSearchAPI(1, url);
  }
  
  function renderLoadMoreButton(pagination, isLoading = false) {
    if (!pagination || !pagination.hasNextPage) return '';
    
    // Get translation for "Show more" - fallback to English if not available
    const showMoreText = window.theme?.strings?.general?.pagination?.show_more || 'Show more';
    const loadingText = window.theme?.strings?.general?.pagination?.loading_text || 'Loading...';
    
    return `
      <div class="pagination-wrapper text-center js-pagination js-api-load-more">
        <button 
          type="button"
          class="button button--secondary js-api-load-more-button${isLoading ? ' loading' : ''}"
          aria-label="${isLoading ? loadingText : showMoreText}"
          ${isLoading ? 'disabled' : ''}>
          ${isLoading ? `
            <span class="button__spinner">
              <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
              </svg>
            </span>
            ${loadingText}
          ` : showMoreText}
        </button>
      </div>
    `;
  }
  
  function attachLoadMoreListener() {
    const loadMoreButton = document.querySelector('.js-api-load-more-button');
    if (!loadMoreButton) return;
    
    // Remove any existing listeners by cloning and replacing
    const newButton = loadMoreButton.cloneNode(true);
    loadMoreButton.parentNode.replaceChild(newButton, loadMoreButton);
    
    newButton.addEventListener('click', function() {
      // Dispatch event instead of handling directly
      window.dispatchEvent(new CustomEvent('loadMoreProducts', {
        detail: { currentPage: currentPage }
      }));
    });
  }
  
  function showButtonLoadingState() {
    const loadMoreButton = document.querySelector('.js-api-load-more-button');
    if (!loadMoreButton) return;
    
    const loadingText = window.theme?.strings?.general?.pagination?.loading_text || 'Loading...';
    
    // Disable button and add loading class
    loadMoreButton.disabled = true;
    loadMoreButton.classList.add('loading');
    loadMoreButton.setAttribute('aria-label', loadingText);
    
    // Update button content with spinner
    loadMoreButton.innerHTML = `
      <span class="button__spinner">
        <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
          <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
        </svg>
      </span>
      ${loadingText}
    `;
  }
  
  // Listen for load more event
  window.addEventListener('loadMoreProducts', async function(event) {
    const nextPage = currentPage + 1;
    
    // Show loading state on button
    showButtonLoadingState();
    
    try {
      await loadMoreProducts(nextPage);
      // Button will be replaced by loadMoreProducts, so no need to reset here
    } catch (error) {
      console.error('Error loading more products:', error);
    }
  });
  
  async function loadMoreProducts(page) {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return;
    
    const { handles: productHandles, pagination } = await fetchSearchAPI(page);
    
    if (productHandles.length === 0) {
      // No more products, remove the button
      const loadMoreWrapper = document.querySelector('.js-api-load-more');
      if (loadMoreWrapper) {
        loadMoreWrapper.remove();
      }
      return;
    }
    
    // Fetch all product cards in parallel
    const cardPromises = productHandles.map(handle => fetchProductCardHTML(handle));
    const cardResults = await Promise.allSettled(cardPromises);
    
    // Append cards to the grid
    cardResults.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value) {
        productGrid.insertAdjacentHTML('beforeend', result.value);
      } else if (result.status === 'rejected') {
        console.error(`Error fetching product card for ${productHandles[index]}:`, result.reason);
      }
    });
    
    // Load quick-add.js for dynamically inserted product cards
    document.querySelectorAll('lazy-script[data-watch-element*="product-card"]').forEach(lazyScript => {
      if (lazyScript.loadScript) lazyScript.loadScript();
    });
    
    // Update product count
    const productCountElements = document.querySelectorAll('.js-product-count');
    if (productCountElements.length > 0 && pagination?.totalCount) {
      const countText = `${pagination.totalCount} ${pagination.totalCount === 1 ? 'product' : 'products'}`;
      productCountElements.forEach(elem => {
        elem.classList.remove('loading');
        elem.textContent = countText;
      });
    }
    
    // Remove old load more button (which is in loading state)
    const oldLoadMoreWrapper = document.querySelector('.js-api-load-more');
    if (oldLoadMoreWrapper) {
      oldLoadMoreWrapper.remove();
    }
    
    // Update current page
    currentPage = page;
    
    // Render new load more button if there are more pages
    if (pagination && pagination.hasNextPage) {
      const paginationContainer = productGrid.closest('.collection__products') || productGrid.parentElement;
      if (paginationContainer) {
        paginationContainer.insertAdjacentHTML('beforeend', renderLoadMoreButton(pagination, false));
        attachLoadMoreListener();
      }
    }
    
    if (typeof window.renderSelects === 'function') {
      window.renderSelects();
    }
  }
  
  async function fetchProductCardHTML(handle) {
    const response = await fetch(`/products/${handle}?section_id=product-card`);
    if (!response.ok) {
      throw new Error(`Request failed: ${response.status}`);
    }
    const html = await response.text();
    
    // Parse HTML using DOMParser for robust extraction
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Find the section wrapper div by ID
    const sectionWrapper = doc.getElementById('shopify-section-product-card');
    
    // If wrapper found, return its innerHTML; otherwise return the original HTML
    if (sectionWrapper) {
      return sectionWrapper.innerHTML.trim();
    }
    
    return html;
  }
  
  function showLoadingState(productGrid) {
    const collectionContainer = productGrid.closest('.collection');
    if (!collectionContainer) return;
    const sortBar = document.querySelector('.js-sort-count-bar');
    if (sortBar) sortBar.style.visibility = 'hidden';

    // Use existing overlay if possible, otherwise create one
    let loadingOverlay = collectionContainer.querySelector('.loading-overlay');
    if (!loadingOverlay) {
      loadingOverlay = document.createElement('div');
      loadingOverlay.className = 'loading-overlay gradient';
      collectionContainer.prepend(loadingOverlay);
    }
    
    // Ensure spinner is present
    if (!loadingOverlay.querySelector('.spinner')) {
      loadingOverlay.innerHTML = `
        <div class="api-loading-content">
          <span class="loading__spinner">
            <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
              <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
            </svg>
          </span>
        </div>
      `;
    }

    // Toggle loading class to show overlay and apply styles via CSS
    collectionContainer.classList.add('loading');
  }
  
  function hideLoadingState(productGrid) {
    const collectionContainer = productGrid.closest('.collection');
    if (!collectionContainer) return;

    // Remove loading class to hide overlay and restore styles via CSS
    collectionContainer.classList.remove('loading');
    const sortBar = document.querySelector('.js-sort-count-bar');
    if (sortBar) sortBar.style.visibility = '';
  }

  function setApiLoading(isLoading) {
    const spinners = document.querySelectorAll('.js-api-loading-spinner');
    spinners.forEach((spinner) => {
      spinner.classList.toggle('hidden', !isLoading);
      spinner.classList.toggle('spinning-complete', !isLoading);
    });
    
    document.querySelectorAll('.js-product-count').forEach((countElem) => {
      countElem.classList.toggle('loading', isLoading);
    });
  }
  
  async function fetchFilterData() {
    const productGridContainer = document.getElementById('ProductGridContainer');
    const useCustomFilters = productGridContainer?.dataset?.useCustomFilters === 'true';
    
    if (!useCustomFilters) return;
    
    // Reset to page 1 for filter data fetch
    currentPage = 1;
    
    setApiLoading(true);
    try {
      await fetchSearchAPI(1);
    } finally {
      setApiLoading(false);
    }
  }

  async function renderProductsFromAPI(url = null) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    const useCustomFilters = productGridContainer?.dataset?.useCustomFilters === 'true';
    
    // When custom filters are enabled, always render products (even with no filters)
    // For other pages, only render if search params exist
    if (!useCustomFilters && !hasSearchParams(url)) {
      fetchFilterData();
      return;
    }
    
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) {
      setTimeout(() => renderProductsFromAPI(url), 100);
      return;
    }
    
    setApiLoading(true);
    showLoadingState(productGrid);
    const gridClasses = productGrid.className;
    
    try {
      const { handles: productHandles, pagination } = await fetchProductHandles(url);
      
      if (productHandles.length === 0) {
        // Atomic update for empty state
        productGrid.innerHTML = `
          <div style="width: 100% !important; max-width: 100% !important; flex-basis: 100% !important;">
            <div class="title-wrapper center py-5" style="padding: 4rem 2rem;">
              <div style="max-width: 500px; margin: 0 auto;">
                <svg style="width: 64px; height: 64px; margin: 0 auto 1.5rem; display: block; opacity: 0.5;" aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <p class="h4-size mb-2" style="font-weight: 600; margin-bottom: 0.75rem;">No products found</p>
                <p style="color: rgba(var(--color-foreground), 0.7); margin-bottom: 1.5rem;">We couldn't find any products matching your filters. Try adjusting your search criteria or clearing some filters.</p>
                <a href="${window.location.pathname}" class="button button--secondary" style="display: inline-block; margin-top: 0.5rem;">Clear all filters</a>
              </div>
            </div>
          </div>
        `;
        productGrid.className = gridClasses;
        document.querySelectorAll('.js-product-count').forEach(elem => {
          elem.classList.remove('loading');
          elem.textContent = '0 products';
        });
        // Remove any existing "Show more" button when no products are found
        const oldButton = document.querySelector('.js-api-load-more');
        if (oldButton) oldButton.remove();
        return;
      }
      
      // Fetch all product cards first
      const cardPromises = productHandles.map(handle => fetchProductCardHTML(handle));
      const cardResults = await Promise.allSettled(cardPromises);
      
      // Build complete HTML string before clearing to prevent flicker
      let newHTML = '';
      cardResults.forEach((result) => {
        if (result.status === 'fulfilled' && result.value) {
          newHTML += result.value;
        }
      });
      
      // Atomic update: replace content in one operation
      productGrid.innerHTML = newHTML;
      productGrid.className = gridClasses;
      
      // Load quick-add.js for dynamically inserted product cards
      document.querySelectorAll('lazy-script[data-watch-element*="product-card"]').forEach(lazyScript => {
        if (lazyScript.loadScript) lazyScript.loadScript();
      });
      
      // Reset current page to 1 for new filter/search
      currentPage = 1;
      
      // Update product count
      const count = pagination?.totalCount ?? productHandles.length;
      const countText = `${count} ${count === 1 ? 'product' : 'products'}`;
      document.querySelectorAll('.js-product-count').forEach(elem => {
        elem.classList.remove('loading');
        elem.textContent = countText;
      });
      
      // Render load more button
      const oldButton = document.querySelector('.js-api-load-more');
      if (oldButton) oldButton.remove();
      
      if (pagination?.hasNextPage) {
        const container = productGrid.closest('.collection__products') || productGrid.parentElement;
        if (container) {
          container.insertAdjacentHTML('beforeend', renderLoadMoreButton(pagination));
          attachLoadMoreListener();
        }
      }
      
      if (typeof window.renderSelects === 'function') {
        window.renderSelects();
      }
      
    } catch (error) {
      console.error('Error rendering products:', error);
      productGrid.innerHTML = `
        <li class="grid__item" style="width: 100% !important; max-width: 100% !important; flex-basis: 100% !important;">
          <div class="title-wrapper center">
            <p class="h4-size mb-1">Error loading products</p>
            <p>${error.message || 'Please try again later.'}</p>
          </div>
        </li>
      `;
    } finally {
      setApiLoading(false);
      hideLoadingState(productGrid);
    }
  }
  
  // Initialize on page load
  function initialize() {
    fetchFilterData();
    renderProductsFromAPI();
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  let previousUrl = window.location.search;
  window.navigation.addEventListener('navigate', (e) => {
    if (e.destination.sameDocument) {
      if (e.destination.url !== previousUrl) {
        renderProductsFromAPI(e.destination.url);
      }
      previousUrl = e.destination.url;
    }
  });
})();
